import { RefObject, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { registerChannelSwitcherSlot } from '@/components/advertisement';
import { selectAds } from '@/store/slices/ads';
import { ScreenSize } from '@/types/views/generic';

import useBreakpoint from './use-breakpoint';

const SIDE_BREAKPOINTS: readonly ScreenSize[] = ['lg', 'xl', '2xl', '3xl'];
const TABLET_BREAKPOINT: ScreenSize = 'md';
const REGISTER_MS = 150;

const desktopLayout = (isMobile: boolean, bp: string) => {
  if (isMobile) {
    return null;
  }

  if (bp === TABLET_BREAKPOINT) {
    return 'tablet';
  }

  if (SIDE_BREAKPOINTS.includes(bp as ScreenSize)) {
    return 'desktop';
  }

  return null;
};

const useChannelSwitcherAdsRegister = (
  active: boolean,
  isMobileSlot: boolean,
  containerRef: RefObject<HTMLDivElement | null>,
  slotKey?: string,
) => {
  const { currentBreakpoint, isMobile } = useBreakpoint();
  const currentBreakpointRef = useRef(currentBreakpoint);
  currentBreakpointRef.current = currentBreakpoint;

  const {
    adsEnabled,
    deactivateAdSlots,
    adChannel,
    initializedAdChannel,
    adsViewportRevision,
  } = useSelector(selectAds);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRunRef = useRef<{ key: string; revision: number } | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const inactive = !active || isMobileSlot !== isMobile;
    const key = slotKey ?? (isMobileSlot ? 'mobile' : 'desktop');

    if (inactive) {
      lastRunRef.current = null;

      return undefined;
    }

    const container = containerRef.current;
    const canRegister =
      adsEnabled &&
      !deactivateAdSlots &&
      adChannel &&
      initializedAdChannel === adChannel &&
      typeof window !== 'undefined' &&
      window.admTagMan;

    if (!container || !canRegister) {
      return undefined;
    }

    const layoutChanged = !lastRunRef.current || lastRunRef.current.key !== key;
    const viewportChanged =
      !lastRunRef.current ||
      lastRunRef.current.revision !== adsViewportRevision;

    if (
      !layoutChanged &&
      !viewportChanged &&
      container.classList.contains('adm-loaded') &&
      container.childElementCount > 0
    ) {
      return undefined;
    }

    timerRef.current = setTimeout(() => {
      const target = containerRef.current;

      if (!target || !adChannel) {
        return;
      }

      registerChannelSwitcherSlot(
        target,
        adChannel,
        isMobileSlot,
        currentBreakpointRef.current,
      );
      lastRunRef.current = { key, revision: adsViewportRevision };
    }, REGISTER_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [
    active,
    isMobile,
    isMobileSlot,
    slotKey,
    adChannel,
    adsEnabled,
    deactivateAdSlots,
    initializedAdChannel,
    adsViewportRevision,
    containerRef,
  ]);
};

export const useChannelSwitcherMobileAd = (
  isOpen: boolean,
  containerRef: RefObject<HTMLDivElement | null>,
) => useChannelSwitcherAdsRegister(isOpen, true, containerRef, 'mobile');

export const useChannelSwitcherDesktopAds = ({
  isOpen,
  sideRef,
  bottomRef,
}: {
  isOpen: boolean;
  sideRef: RefObject<HTMLDivElement | null>;
  bottomRef: RefObject<HTMLDivElement | null>;
}) => {
  const { isMobile, currentBreakpoint } = useBreakpoint();
  const layout = desktopLayout(isMobile, currentBreakpoint);
  const containerRef = layout === 'desktop' ? sideRef : bottomRef;

  useChannelSwitcherAdsRegister(
    isOpen && layout !== null,
    false,
    containerRef,
    layout ?? undefined,
  );
};

export default useChannelSwitcherMobileAd;
